import { useQuery } from '@tanstack/react-query';
import { permissionsApi } from '@/lib/api/permissions.api';
import { apiClient } from '@/lib/api/api-client';
import { queryKeys } from '../query-keys';
import { useOrganizationStore } from '@/store/organization.store';
import type {
  PermissionDefinition,
  PermissionSet,
  PermissionSetAssignment,
  PermissionAssignment,
  ModuleTypeEnum,
} from '@scrambl/shared';

// Extended types matching store types
interface ExtendedPermissionSet extends PermissionSet {
  permissionSetItems?: Array<{
    id?: string;
    permissionSetId: string;
    permissionDefinitionId: string;
    permissionDefinition?: PermissionDefinition;
  }>;
}

interface ExtendedPermissionAssignment extends PermissionAssignment {
  permissionDefinition?: PermissionDefinition;
}

interface ExtendedPermissionSetAssignment extends PermissionSetAssignment {
  permissionSet?: ExtendedPermissionSet;
}

interface MemberPermissionsResponse {
  directPermissions: ExtendedPermissionAssignment[];
  permissionSets: ExtendedPermissionSetAssignment[];
}

/**
 * Hook to fetch permission definitions, optionally filtered by module type
 */
export function usePermissionDefinitionsQuery(
  moduleType?: ModuleTypeEnum,
  options?: { enabled?: boolean; forceRefresh?: boolean }
) {
  const { currentOrganization } = useOrganizationStore();

  return useQuery<PermissionDefinition[]>({
    queryKey: queryKeys.permissions.definitions.list(moduleType),
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('Organization ID is required');
      }
      return permissionsApi.getDefinitions(moduleType, options?.forceRefresh || false);
    },
    enabled: (options?.enabled !== false && !!currentOrganization?.id) ?? false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch all permission sets
 */
export function usePermissionSetsQuery(options?: { enabled?: boolean }) {
  const { currentOrganization } = useOrganizationStore();

  return useQuery<ExtendedPermissionSet[]>({
    queryKey: queryKeys.permissions.sets.list(),
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('Organization ID is required');
      }
      return permissionsApi.getPermissionSets(currentOrganization.id, false);
    },
    enabled: (options?.enabled !== false && !!currentOrganization?.id) ?? false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch permissions for a specific member
 * Note: The API returns PermissionSetAssignment[], but we need to check the actual response structure
 * For now, we'll query the endpoint directly to get both directPermissions and permissionSets
 */
export function useMemberPermissionsQuery(
  memberId: string | undefined,
  options?: { enabled?: boolean }
) {
  const { currentOrganization } = useOrganizationStore();

  return useQuery<MemberPermissionsResponse>({
    queryKey: queryKeys.permissions.memberPermissions(memberId!),
    queryFn: async () => {
      if (!memberId) {
        throw new Error('Member ID is required');
      }
      if (!currentOrganization?.id) {
        throw new Error('Organization ID is required');
      }
      // The actual endpoint returns both directPermissions and permissionSets
      const response = await apiClient<MemberPermissionsResponse>(`/permissions/members/${memberId}`);
      return {
        directPermissions: response.directPermissions || [],
        permissionSets: response.permissionSets || [],
      };
    },
    enabled: (options?.enabled !== false && !!memberId && !!currentOrganization?.id) ?? false,
  });
}

